// Updated: 21-04-2026
// Changes:
// - Fixed image URL (was using /api prefix which broke image paths)
// - Added back button (useNavigate -1)
// - All saved fields now displayed: category, amountPerHour, areaunit,
//   ownerName, ownerContactNumber, amenities, propertyOtherReason
// - Price formatted in INR (Lakhs / Crores)
// - Image gallery support (shows all images, not just first)
// - Status badge with color class
// - Graceful fallback if no images
// - Owner details section only visible to admins

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Bed,
  Bath,
  Maximize,
  ArrowLeft,
  Phone,
  User,
  Clock,
  Tag,
  Building2,
  Home,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Zap,
} from "lucide-react";
import "../../css/properties/propertydetails.css";
import { useAuth } from "../../api/authContext";

/* ── API bases ──────────────────────────────────────────────────── */
const API_BASE     = "https://apicrm.zenrealestate.in";          // for images
const API_ENDPOINT = "https://apicrm.zenrealestate.in/api";      // for data

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800";

/* ── Helpers ────────────────────────────────────────────────────── */
function buildImageUrl(imgPath) {
  if (!imgPath) return FALLBACK_IMAGE;
  if (imgPath.startsWith("http")) return imgPath;
  return `${API_BASE}/${imgPath.replace(/^\/+/, "")}`;
}

function formatINR(value) {
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  if (isNaN(num) || num === 0) return `₹${value}`;
  if (num >= 10_000_000)
    return `₹${(num / 10_000_000).toFixed(2).replace(/\.?0+$/, "")} Cr`;
  if (num >= 100_000)
    return `₹${(num / 100_000).toFixed(2).replace(/\.?0+$/, "")} L`;
  if (num >= 1_000)
    return `₹${(num / 1_000).toFixed(2).replace(/\.?0+$/, "")} K`;
  return `₹${num}`;
}

const statusColors = {
  available:          "status-available",
  under_offer:        "status-under-offer",
  sold:               "status-sold",
  rented:             "status-rented",
  unavailable:        "status-unavailable",
  under_construction: "status-construction",
  other:              "status-other",
};

/* ── Component ──────────────────────────────────────────────────── */
export default function PropertyDetails() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { isAdmin }  = useAuth();
  const [property, setProperty] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        console.log(`Fetching from: ${API_ENDPOINT}/properties/${id}`);
        const response = await fetch(`${API_ENDPOINT}/properties/${id}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("Property data received:", data);
        setProperty(data);
      } catch (err) {
        console.error("Failed to load property:", err);
        setError(err.message);
      }
    };

    fetchProperty();
  }, [id]);

  if (error) {
    return (
      <div className="pd-loading">
        <p style={{ color: "red" }}>Error: {error}</p>
        <button onClick={() => navigate(-1)} style={{ marginTop: "20px", padding: "10px 20px", cursor: "pointer" }}>
          Go Back
        </button>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pd-loading">
        <div className="pd-spinner" />
        <p>Loading property details…</p>
      </div>
    );
  }

  const images =
    property.images && property.images.length > 0
      ? property.images.map(buildImageUrl)
      : [FALLBACK_IMAGE];

  const isRent       = property.listingType === "rent";
  const categoryLabel =
    property.propertyCategory === "commercial" ? "Commercial" : "Residential";
  const CategoryIcon  =
    categoryLabel === "Commercial" ? Building2 : Home;

  return (
    <div className="pd-page-modern">
      {/* ── Back button ── */}
      <button className="pd-back-btn-modern" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} />
      </button>

      {/* ══ HERO SECTION — Image + Main Info ══════════════════════ */}
      <div className="pd-hero">
        <div className="pd-image-container">
          <img
            src={images[activeImg]}
            alt={property.propertyName}
            className="pd-hero-image"
            onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
          />
          <div className="pd-badges-top">
            <span className="pd-listing-badge-modern">
              {isRent ? "For Rent" : "For Sale"}
            </span>
            <span className={`pd-category-badge-modern ${categoryLabel === "Commercial" ? "commercial" : "residential"}`}>
              {categoryLabel}
            </span>
          </div>

          {/* Image nav — shown only when multiple images */}
          {images.length > 1 && (
            <div className="pd-image-nav">
              <button
                className="pd-nav-btn"
                onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)}
              >
                <ChevronLeft size={20} />
              </button>
              <span className="pd-image-counter">{activeImg + 1} / {images.length}</span>
              <button
                className="pd-nav-btn"
                onClick={() => setActiveImg((i) => (i + 1) % images.length)}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Quick info */}
        <div className="pd-quick-info">
          <h1 className="pd-title-modern">{property.propertyName}</h1>
          <div className="pd-location-modern">
            <MapPin size={18} />
            <span>{property.location || "—"}</span>
          </div>
          <div className="pd-price-section">
            <span className="pd-price-modern">{formatINR(property.price)}</span>
            {isRent && <span className="pd-price-suffix-modern">/month</span>}
          </div>
        </div>
      </div>

      {/* ══ CONTENT SECTION ════════════════════════════════════════ */}
      <div className="pd-content-modern">

        {/* Features grid */}
        {(property.bedrooms || property.washrooms || property.area) && (
          <div className="pd-section-card">
            <h3 className="pd-section-title-modern">Features</h3>
            <div className="pd-features-grid">
              {property.bedrooms && (
                <div className="pd-feature-item">
                  <Bed size={20} />
                  <div>
                    <p className="pd-feature-value">{property.bedrooms}</p>
                    <p className="pd-feature-label">Bedrooms</p>
                  </div>
                </div>
              )}
              {property.washrooms && (
                <div className="pd-feature-item">
                  <Bath size={20} />
                  <div>
                    <p className="pd-feature-value">{property.washrooms}</p>
                    <p className="pd-feature-label">Bathrooms</p>
                  </div>
                </div>
              )}
              {property.area && (
                <div className="pd-feature-item">
                  <Maximize size={20} />
                  <div>
                    <p className="pd-feature-value">{property.area}</p>
                    <p className="pd-feature-label">{property.areaunit}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Details grid */}
        <div className="pd-section-card">
          <h3 className="pd-section-title-modern">Property Details</h3>
          <div className="pd-details-grid">
            {property.propertyType && (
              <div className="pd-detail-row">
                <span className="pd-detail-label">Property Type</span>
                <span className="pd-detail-value">{property.propertyType}</span>
              </div>
            )}
            <div className="pd-detail-row">
              <span className="pd-detail-label">Listing Type</span>
              <span className="pd-detail-value">{isRent ? "For Rent" : "For Sale"}</span>
            </div>
            <div className="pd-detail-row">
              <span className="pd-detail-label">Status</span>
              <span className={`pd-status-badge-modern ${statusColors[property.status] || ""}`}>
                {property.status?.replace(/_/g, " ")}
              </span>
            </div>
            {property.status === "other" && property.propertyOtherReason && (
              <div className="pd-detail-row pd-detail-full">
                <span className="pd-detail-label">Status Reason</span>
                <span className="pd-detail-value">{property.propertyOtherReason}</span>
              </div>
            )}
          </div>
        </div>

        {/* Owner Info */}
        {isAdmin && (property.ownerName || property.ownerContactNumber) && (
          <div className="pd-section-card">
            <h3 className="pd-section-title-modern">Owner Information</h3>
            <div className="pd-owner-section">
              {property.ownerName && (
                <div className="pd-owner-item">
                  <User size={18} />
                  <div>
                    <p className="pd-owner-label">Name</p>
                    <p className="pd-owner-value">{property.ownerName}</p>
                  </div>
                </div>
              )}
              {property.ownerContactNumber && (
                <div className="pd-owner-item">
                  <Phone size={18} />
                  <div>
                    <p className="pd-owner-label">Contact</p>
                    <p className="pd-owner-value">{property.ownerContactNumber}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Description */}
        {property.description && (
          <div className="pd-section-card">
            <h3 className="pd-section-title-modern">Description</h3>
            <p className="pd-description-text">{property.description}</p>
          </div>
        )}

        {/* Amenities */}
        {property.amenities && property.amenities.length > 0 && (
          <div className="pd-section-card">
            <h3 className="pd-section-title-modern">Amenities</h3>
            <div className="pd-amenities-grid">
              {property.amenities.map((a, i) => (
                <div key={i} className="pd-amenity-item">
                  <CheckCircle2 size={16} />
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}